import React from "react";
import { ExternalLink, Minimize2 } from "lucide-react";
import { joinClasses, DISPATCH_FRAME_RADIUS_CLASS_NAME, DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME } from "./ResultsPrimitives.jsx";
import { FLOATING_MAP_SIZE_OPTIONS } from "../utils/floatingRouteMapLayout.js";

const FLOATING_MAP_RESIZE_ZONES = [
  { direction: "north-west", className: "absolute -top-2 -left-2 z-1102 h-5 w-5 cursor-nw-resize touch-none bg-transparent" },
  { direction: "north", className: "absolute -top-2 left-5 right-5 z-1101 h-4 cursor-n-resize touch-none bg-transparent" },
  { direction: "north-east", className: "absolute -top-2 -right-2 z-1102 h-5 w-5 cursor-ne-resize touch-none bg-transparent" },
  { direction: "east", className: "absolute top-5 bottom-5 -right-2 z-1101 w-4 cursor-e-resize touch-none bg-transparent" },
  { direction: "south-east", className: "absolute -bottom-2 -right-2 z-1102 h-5 w-5 cursor-se-resize touch-none bg-transparent" },
  { direction: "south", className: "absolute -bottom-2 left-5 right-5 z-1101 h-4 cursor-s-resize touch-none bg-transparent" },
  { direction: "south-west", className: "absolute -bottom-2 -left-2 z-1102 h-5 w-5 cursor-sw-resize touch-none bg-transparent" },
  { direction: "west", className: "absolute top-5 bottom-5 -left-2 z-1101 w-4 cursor-w-resize touch-none bg-transparent" },
];

export default function FloatingRouteMapSurface({
  ariaLabel = "Carte flottante",
  badgeLabel = "Mode flottant",
  children = null,
  className = "",
  closeLabel = "Réduire",
  isDragging = false,
  isResizing = false,
  onClose = null,
  onDragHandlePointerDown = null,
  onKeyDown = null,
  onPointerCancel = null,
  onPointerMove = null,
  onPointerUp = null,
  onResizeHandlePointerDown = null,
  onSecondaryAction = null,
  panelRef = null,
  secondaryActionActive = false,
  secondaryActionLabel = "",
  size = "standard",
  style = undefined,
  subtitle = "",
  title = "",
  onSizeChange = null,
}) {
  return (
    <div
      ref={panelRef}
      data-dispatch-context-surface="true"
      onKeyDown={onKeyDown}
      onPointerCancel={onPointerCancel}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      role="dialog"
      aria-label={ariaLabel}
      tabIndex={-1}
      className={joinClasses([
        "pointer-events-auto absolute z-60 grid content-start grid-rows-[auto_auto_minmax(0,1fr)] gap-3 max-w-[calc(100vw-1.5rem)] rounded-[8px] border border-[rgba(15,23,42,0.08)] bg-[linear-gradient(180deg,rgba(255,255,255,0.985),rgba(248,250,252,0.97))] p-3 shadow-[0_26px_60px_rgba(15,23,42,0.22)] backdrop-blur-[10px]",
        isDragging ? "cursor-grabbing" : "",
        className,
      ])}
      style={style}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="grid min-w-0 gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className={joinClasses([
              "inline-flex w-fit items-center border border-border-accent-muted bg-surface-accent-tint px-2.5 py-1 text-[0.62rem] font-extrabold uppercase tracking-[0.12em] text-(--color-legacy-teal)",
              DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
            ])}>
              {badgeLabel}
            </span>
            {title ? <span className="truncate text-[0.72rem] font-semibold text-text">{title}</span> : null}
          </div>
          {subtitle ? <span className="text-[0.68rem] leading-5 text-content-muted">{subtitle}</span> : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {typeof onSizeChange === "function" ? (
            <div className={joinClasses([
              "inline-flex flex-wrap items-center gap-1 border border-[rgba(148,163,184,0.18)] bg-white p-1",
              DISPATCH_FRAME_RADIUS_CLASS_NAME,
            ])}>
              {FLOATING_MAP_SIZE_OPTIONS.map((option) => {
                const isActiveOption = size === option.key;

                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-pressed={isActiveOption ? "true" : "false"}
                    onClick={() => {
                      onSizeChange(option.key);
                    }}
                    className={joinClasses([
                      "inline-flex items-center px-2.5 py-1.5 text-[0.68rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
                      DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
                      isActiveOption
                        ? "border border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                        : "border border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                    ])}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          ) : null}

          {secondaryActionLabel && typeof onSecondaryAction === "function" ? (
            <button
              type="button"
              onClick={onSecondaryAction}
              aria-pressed={secondaryActionActive ? "true" : "false"}
              className={joinClasses([
                "inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-[0.68rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
                DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
                secondaryActionActive
                  ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
              ])}
            >
              <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
              <span>{secondaryActionLabel}</span>
            </button>
          ) : null}

          <button
            type="button"
            onClick={onClose}
            className={joinClasses([
              "inline-flex items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1.5 text-[0.68rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
              DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
            ])}
          >
            <Minimize2 aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
            <span>{closeLabel}</span>
          </button>
        </div>
      </div>

      <div
        onPointerDown={onDragHandlePointerDown}
        className={joinClasses([
          "flex cursor-grab items-center justify-between rounded-[8px] border border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.92)] px-3 py-2 text-[0.68rem] text-content-copy select-none",
          isDragging ? "cursor-grabbing" : "",
        ])}
      >
        <span className="font-semibold text-text">Glisser pour déplacer</span>
        <span className="text-right leading-5 text-content-muted">Échap ferme le panneau flottant.</span>
      </div>

      <div className="relative z-0 h-full min-h-0 min-w-0">
        {children}
      </div>

      {typeof onResizeHandlePointerDown === "function" ? (
        <>
          {FLOATING_MAP_RESIZE_ZONES.map((zone) => (
            <span
              key={zone.direction}
              aria-hidden="true"
              data-floating-map-resize-direction={zone.direction}
              onPointerDown={(event) => {
                onResizeHandlePointerDown(event, zone.direction);
              }}
              className={zone.className}
            />
          ))}
        </>
      ) : null}
    </div>
  );
}
